import i18n from "@/i18n";

export const FULL_YEAR_MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

// Returns short month name by number and locale (e.g., 1 → "Jan")
const getMonthName = (monthNumber: number, locale: string): string =>
    new Date(0, monthNumber - 1).toLocaleString(locale, { month: "short" });

// Formats a range of month strings (e.g., ["2024-01", "2024-05"]) into "January – May 2024"
export const formatMonthRange = (monthStrings: string[]): string => {
    if (monthStrings.length === 0) return "";
    const locale = i18n.language
    const formatter = new Intl.DateTimeFormat(locale, { month: "long" });
    const startDate = new Date(monthStrings[0]);
    const endDate = new Date(monthStrings[monthStrings.length - 1]);

    const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

    const startMonth = capitalize(formatter.format(startDate));
    const endMonth = capitalize(formatter.format(endDate));
    const endYear = endDate.getFullYear();

    return `${startMonth} – ${endMonth} ${endYear}`;
};

// Converts an array of "YYYY-MM" strings to abbreviated month names (e.g., ["2024-01"] → ["Jan"])
export const getAbbreviatedMonthNames = (monthStrings: string[]): string[] => {
    const locale = i18n.language
    return monthStrings.map((monthStr) => {
        const [year, month] = monthStr.split("-");
        const date = new Date(Number(year), Number(month) - 1);
        const fullMonthName = date.toLocaleString(locale, { month: "long" });
        return fullMonthName.slice(0, 3).charAt(0).toUpperCase() + fullMonthName.slice(1, 3).toLowerCase();
    });
};

// Ensures a given string (usually month) is formatted to a capitalized 3-letter abbreviation (e.g., "march" → "Mar")
export const formatToShortMonth = (value?: string | null): string => {
    if (!value) return '';
    const short = value.slice(0, 3).toLowerCase();
    return short.charAt(0).toUpperCase() + short.slice(1);
};

// Formats a given "YYYY-MM" string into a capitalized 3-letter month abbreviation (e.g., "2024-03" → "Mar")
export const formatMonthLabel = (monthStr: string) => {
    const [year, month] = monthStr.split('-').map(Number);
    if (!year || !month) return monthStr;

    const locale = i18n.language
    const date = new Date(year, month - 1);
    const formatter = new Intl.DateTimeFormat(locale, { month: 'short' });
    const shortMonth = formatter.format(date).slice(0, 3);

    return shortMonth.charAt(0).toUpperCase() + shortMonth.slice(1);
};

// Formats numeric months and year into start/end strings (e.g., "Jan - Mar | 2025")
export const formatDateRange = (months: number[], year: number) => {
    const locale = i18n.language
    const sortedMonths = [...months].sort((a, b) => a - b);
    const startMonth = formatToShortMonth(getMonthName(sortedMonths[0], locale));
    const endMonth = formatToShortMonth(getMonthName(sortedMonths[sortedMonths.length - 1], locale));

    const endDateStr = `${startMonth} - ${endMonth} | ${year}`;
    const startDateStr = `${startMonth} - ${endMonth} | ${year - 1}`;
    return { startDateStr, endDateStr };
};